import React, { useState, useEffect } from 'react';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

interface PrescriptionDetailsProps {
  doctorId: number;
  clinicId: number;
  appointmentId: string | number;
  onMoveToBill?: (medicineData: any) => void;
}

const PrescriptionDetails: React.FC<PrescriptionDetailsProps> = ({ 
  doctorId, 
  clinicId, 
  appointmentId,
  onMoveToBill
}) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [details, setDetails] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch prescription details
  useEffect(() => {
    const fetchPrescriptionDetails = async () => {
      if (!doctorId || !clinicId || !appointmentId) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const authToken = getAuthToken();
        if (!authToken) {
          throw new Error('Authentication token not found');
        }

        const response = await fetch(
          `https://pharma-amd-0-1-42.onrender.com/pharma/api/prescriptions/${doctorId}/${clinicId}/${appointmentId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch prescription details: ${response.status}`);
        }

        const data = await response.json();
        console.log('Prescription details response:', data);
        console.log('Prescription items:', data.data?.prescription_items);
        setDetails(data);
      } catch (err: any) {
        console.error('Error fetching prescription details:', err);
        setError('Unable to load prescription details. Please try again in a moment.');
        setDetails(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrescriptionDetails();
  }, [doctorId, clinicId, appointmentId]);

  if (!doctorId || !clinicId || !appointmentId) {
    return (
      <div style={{
        padding: isMobile ? '1rem' : '1.5rem',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '0.875rem'
      }}>
        Please provide doctor ID, clinic ID, and appointment ID to view prescription details.
      </div>
    );
  }

  // Get all prescription items for table
  const prescriptionItems = details?.data?.prescription_items || [];

  return (
    <div
      style={{
        background: 'white',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '1.5rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.5rem',
          paddingBottom: '0.75rem',
          borderBottom: '2px solid #e5e7eb',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#111827',
            fontWeight: 700,
            fontSize: isMobile ? '1rem' : '1.125rem',
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <span>Prescription Details</span>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#4b5563',
            padding: '2rem',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: '2px solid rgba(55,65,81,0.25)',
              borderTopColor: '#4b5563',
              animation: 'spin 1s linear infinite',
            }}
          />
          <span>Loading prescription details...</span>
        </div>
      ) : error ? (
        <div
          style={{
            fontSize: '0.875rem',
            color: '#b91c1c',
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          {error}
        </div>
      ) : details ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
        >
          {!prescriptionItems || prescriptionItems.length === 0 ? (
            <div style={{ 
              fontSize: '0.875rem', 
              color: '#6b7280', 
              padding: '1.5rem', 
              textAlign: 'center',
              background: '#f9fafb',
              borderRadius: '8px',
            }}>
              No prescription items found in the response.
            </div>
          ) : (
            <>
              {isMobile ? (
                /* Mobile Card View */
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  {prescriptionItems.map((item: any, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        padding: '1rem',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      {/* Item Number and Source */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.75rem',
                          paddingBottom: '0.75rem',
                          borderBottom: '2px solid #f3f4f6',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          Item #{idx + 1}
                        </div>
                        {item.source === 'inventory' ? (
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            <span>Inventory</span>
                          </div>
                        ) : (
                          <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                            {item.source || '-'}
                          </span>
                        )}
                      </div>

                      {/* Field-Value Pairs */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.75rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Medicine Name:
                          </span>
                          <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#111827', textAlign: 'right', flex: '1', wordBreak: 'break-word' }}>
                            {item.medicine_name || '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Company:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1', wordBreak: 'break-word' }}>
                            {item.medicine_company || '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Composition:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1', wordBreak: 'break-word' }}>
                            {item.medicine_composition || '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Batch No:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1' }}>
                            {item.batch_no || '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Expiry Date:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1' }}>
                            {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Quantity:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1' }}>
                            {item.quantity != null ? item.quantity : '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Dosage:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1', wordBreak: 'break-word' }}>
                            {item.dosage || '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Frequency:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1', wordBreak: 'break-word' }}>
                            {item.frequency || '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Duration:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1', wordBreak: 'break-word' }}>
                            {item.duration || '-'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', flex: '0 0 40%' }}>
                            Instructions:
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'right', flex: '1', wordBreak: 'break-word' }}>
                            {item.instructions || '-'}
                          </span>
                        </div>

                        {/* Action Button */}
                        {item.source === 'inventory' && onMoveToBill && (
                          <div
                            style={{
                              marginTop: '0.75rem',
                              paddingTop: '0.75rem',
                              borderTop: '1px solid #e5e7eb',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <button
                              onClick={() => {
                                const gstVal = item.gst_percent ?? item.gst_rate ?? item.gst ?? item.gst_percentage ?? item.gst_slab;
                                const medicineData: any = {
                                  'Medicine Name': item.medicine_name !== undefined && item.medicine_name !== null ? String(item.medicine_name) : '',
                                  'Company': item.medicine_company !== undefined && item.medicine_company !== null ? String(item.medicine_company) : '',
                                  'Composition': item.medicine_composition !== undefined && item.medicine_composition !== null ? String(item.medicine_composition) : '',
                                  'Batch No': item.batch_no !== undefined && item.batch_no !== null ? String(item.batch_no) : '',
                                  'Expiry Date': item.expiry_date !== undefined && item.expiry_date !== null ? String(item.expiry_date) : '',
                                  // Do NOT include Quantity - user wants to enter it manually
                                  // 'Quantity': item.quantity || 0,
                                };
                                
                                // Add optional fields if they exist
                                if (item.quantity_type !== undefined && item.quantity_type !== null) {
                                  medicineData['Qty Type'] = String(item.quantity_type);
                                }
                                if (item.tablets_per_strip !== undefined && item.tablets_per_strip !== null) {
                                  medicineData['Tablets/Strip'] = Number(item.tablets_per_strip);
                                }
                                if (item.unit_cost !== undefined && item.unit_cost !== null) {
                                  medicineData['Unit Cost'] = Number(item.unit_cost);
                                }
                                if (item.selling_price !== undefined && item.selling_price !== null) {
                                  medicineData['Selling Price'] = Number(item.selling_price);
                                }
                                if (item.mrp !== undefined && item.mrp !== null) {
                                  medicineData['MRP'] = Number(item.mrp);
                                }
                                if (item.storage_location !== undefined && item.storage_location !== null) {
                                  medicineData['Storage'] = String(item.storage_location);
                                }
                                if (gstVal !== undefined && gstVal !== null && gstVal !== '') {
                                  medicineData['GST%'] = Number(gstVal);
                                }
                                
                                console.log('Moving to bill - medicineData:', medicineData);
                                onMoveToBill(medicineData);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.6rem 1.25rem',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                                width: '100%',
                                justifyContent: 'center',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 5v14M5 12h14" />
                              </svg>
                              <span>Move to Bill</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Desktop Table View with Scrollbar */
                <div
                  style={{
                    overflowX: 'auto',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    WebkitOverflowScrolling: 'touch',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                  }}
                  className="prescription-table-container"
                >
                  <table
                    style={{
                      minWidth: '100%',
                      width: 'max-content',
                      borderCollapse: 'collapse',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                  <thead>
                    <tr
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: 'white',
                      }}
                    >
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)', position: 'sticky', left: 0, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', zIndex: 10 }}>
                        #
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Source
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Medicine Name
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Company
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Composition
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Batch No
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Expiry Date
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Quantity
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Dosage
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Frequency
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Duration
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        Instructions
                      </th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescriptionItems.map((item: any, idx: number) => (
                      <tr
                        key={idx}
                        style={{
                          background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                          transition: 'background 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#eff6ff';
                          const firstCell = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                          if (firstCell) firstCell.style.background = '#eff6ff';
                        }}
                        onMouseLeave={(e) => {
                          const bgColor = idx % 2 === 0 ? '#ffffff' : '#f9fafb';
                          e.currentTarget.style.background = bgColor;
                          const firstCell = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                          if (firstCell) firstCell.style.background = bgColor;
                        }}
                      >
                        <td style={{ padding: '0.875rem 1rem', color: '#4b5563', fontWeight: 600, borderRight: '1px solid #e5e7eb', position: 'sticky', left: 0, background: idx % 2 === 0 ? '#ffffff' : '#f9fafb', zIndex: 5, transition: 'background 0.2s ease' }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', borderRight: '1px solid #e5e7eb' }}>
                          {item.source === 'inventory' ? (
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px',
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                              }}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                              <span>Inventory</span>
                            </div>
                          ) : (
                            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                              {item.source || '-'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#111827', fontWeight: 500, borderRight: '1px solid #e5e7eb', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '200px' }}>
                          {item.medicine_name || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '150px' }}>
                          {item.medicine_company || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '150px' }}>
                          {item.medicine_composition || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                          {item.batch_no || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                          {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                          {item.quantity != null ? item.quantity : '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                          {item.dosage || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                          {item.frequency || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                          {item.duration || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', color: '#6b7280', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '200px', borderRight: '1px solid #e5e7eb' }}>
                          {item.instructions || '-'}
                        </td>
                        <td style={{ padding: '0.875rem 1rem', textAlign: 'center' }}>
                          {item.source === 'inventory' && onMoveToBill ? (
                            <button
                              onClick={() => {
                                const gstVal = item.gst_percent ?? item.gst_rate ?? item.gst ?? item.gst_percentage ?? item.gst_slab;
                                const medicineData: any = {
                                  'Medicine Name': item.medicine_name !== undefined && item.medicine_name !== null ? String(item.medicine_name) : '',
                                  'Company': item.medicine_company !== undefined && item.medicine_company !== null ? String(item.medicine_company) : '',
                                  'Composition': item.medicine_composition !== undefined && item.medicine_composition !== null ? String(item.medicine_composition) : '',
                                  'Batch No': item.batch_no !== undefined && item.batch_no !== null ? String(item.batch_no) : '',
                                  'Expiry Date': item.expiry_date !== undefined && item.expiry_date !== null ? String(item.expiry_date) : '',
                                  // Do NOT include Quantity - user wants to enter it manually
                                  // 'Quantity': item.quantity || 0,
                                };
                                
                                // Add optional fields if they exist
                                if (item.quantity_type !== undefined && item.quantity_type !== null) {
                                  medicineData['Qty Type'] = String(item.quantity_type);
                                }
                                if (item.tablets_per_strip !== undefined && item.tablets_per_strip !== null) {
                                  medicineData['Tablets/Strip'] = Number(item.tablets_per_strip);
                                }
                                if (item.unit_cost !== undefined && item.unit_cost !== null) {
                                  medicineData['Unit Cost'] = Number(item.unit_cost);
                                }
                                if (item.selling_price !== undefined && item.selling_price !== null) {
                                  medicineData['Selling Price'] = Number(item.selling_price);
                                }
                                if (item.mrp !== undefined && item.mrp !== null) {
                                  medicineData['MRP'] = Number(item.mrp);
                                }
                                if (item.storage_location !== undefined && item.storage_location !== null) {
                                  medicineData['Storage'] = String(item.storage_location);
                                }
                                if (gstVal !== undefined && gstVal !== null && gstVal !== '') {
                                  medicineData['GST%'] = Number(gstVal);
                                }
                                
                                console.log('Moving to bill - medicineData:', medicineData);
                                onMoveToBill(medicineData);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                              }}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 5v14M5 12h14" />
                              </svg>
                              <span>Move to Bill</span>
                            </button>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Custom Scrollbar Styling */
        .prescription-table-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .prescription-table-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .prescription-table-container::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 10px;
        }

        .prescription-table-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        }

        /* Firefox Scrollbar */
        .prescription-table-container {
          scrollbar-width: thin;
          scrollbar-color: #3b82f6 #f1f5f9;
        }
      `}</style>
    </div>
  );
};

export default PrescriptionDetails;
